import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN ExpertBio variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 *
 * Variant axes match Figma component properties:
 *  - Size: LG (vertical) | SM (horizontal)
 *  - Border: NONE | Padding Only (includes desktop hover shadow)
 */

/**
 * Container styles for the expert bio wrapper
 */
export const expertBioContainerVariants = cva('flex rounded-xl', {
  variants: {
    size: {
      lg: 'flex-col gap-sm w-full max-w-expert-card h-fit overflow-hidden',
      sm: 'flex-row gap-lg items-center h-expert-card-sm w-full overflow-hidden',
    },
    border: {
      none: 'bg-transparent',
      paddingOnly:
        'group transition-all duration-200 md:hover:bg-white md:hover:shadow-md',
    },
  },
  compoundVariants: [
    { size: 'lg', border: 'paddingOnly', class: 'py-d3 px-xl' },
    { size: 'sm', border: 'paddingOnly', class: 'py-md px-lg' },
  ],
  defaultVariants: {
    size: 'sm',
    border: 'none',
  },
});

/**
 * Name/heading styles — turns primary on hover when border=paddingOnly.
 */
export const expertBioNameVariants = cva('transition-colors duration-200', {
  variants: {
    border: {
      none: '',
      paddingOnly: 'md:group-hover:text-primary-700',
    },
  },
  defaultVariants: {
    border: 'none',
  },
});

/**
 * Image styles based on size variant
 */
export const expertBioImageVariants = cva('object-cover', {
  variants: {
    size: {
      lg: 'w-full h-fit lg:h-expert-card-img',
      sm: 'w-expert-card-img-sm h-expert-card-img-sm flex-shrink-0 object-top',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export type ExpertBioContainerVariantProps = VariantProps<
  typeof expertBioContainerVariants
>;
export type ExpertBioImageVariantProps = VariantProps<
  typeof expertBioImageVariants
>;
